<template>
  <div class="entity-list-page research-campaign-wizard">
    <!-- Header -->
    <header class="list-header wizard-header">
      <div class="wizard-title-group">
        <button
          v-if="selectedCampaign"
          type="button"
          class="back-btn"
          @click="selectedCampaign = null"
        >
          <i class="fas fa-arrow-left" aria-hidden="true"></i>
        </button>
        <div>
          <router-link
            v-if="!selectedCampaign"
            :to="labBackTo"
            class="back-link"
          >← Check Lab</router-link>
          <h1 class="wizard-title">
            {{ selectedCampaign ? selectedCampaign.name : 'Research campaigns' }}
          </h1>
          <p class="list-sub wizard-subtitle">
            {{ selectedCampaign
              ? `${selectedCampaign.business_type} · ${statusLabel(selectedCampaign.status)}`
              : 'Research industries, draft check definitions, and test them — then open results in Check Lab.' }}
          </p>
        </div>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-primary" @click="showNewCampaignModal = true">
          <i class="fas fa-plus" aria-hidden="true"></i> New campaign
        </button>
      </div>
    </header>

    <!-- Campaign List (no campaign selected) -->
    <div v-if="!selectedCampaign" class="campaign-list-view">
      <div v-if="loadingList" class="loading-state">
        <div v-for="n in 3" :key="n" class="skeleton-card" />
      </div>

      <div v-else-if="campaigns.length === 0" class="empty-state">
        <i class="fas fa-seedling empty-icon"></i>
        <h3>No campaigns yet</h3>
        <p>Create your first research campaign to get started.</p>
        <button class="btn-primary" @click="showNewCampaignModal = true">
          <i class="fas fa-plus"></i> Create Campaign
        </button>
      </div>

      <div v-else class="campaign-grid">
        <div
          v-for="campaign in campaigns"
          :key="campaign.id"
          class="campaign-card"
          @click="openCampaign(campaign)"
        >
          <div class="campaign-card-header">
            <span class="campaign-status-badge" :class="statusClass(campaign.status)">
              <i :class="statusIcon(campaign.status)"></i>
              {{ statusLabel(campaign.status) }}
            </span>
            <span v-if="campaign.is_in_progress" class="spinner-sm"></span>
          </div>
          <h3 class="campaign-card-title">{{ campaign.name }}</h3>
          <p class="campaign-card-business">
            <i class="fas fa-building"></i> {{ campaign.business_type }}
          </p>
          <div class="campaign-card-meta">
            <span v-if="campaign.requirement_count">
              <i class="fas fa-clipboard-check"></i> {{ campaign.requirement_count }} requirements
            </span>
            <span v-if="campaign.test_summary && campaign.test_summary.overall_pass_rate !== undefined">
              <i class="fas fa-chart-bar"></i> {{ campaign.test_summary.overall_pass_rate }}% pass rate
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Campaign Detail (4-step pipeline) -->
    <div v-else class="campaign-detail">
      <!-- Action error banner -->
      <div v-if="actionError" class="action-error-banner">
        <i class="fas fa-exclamation-triangle"></i>
        <span>{{ actionError }}</span>
        <button class="reset-inline-btn" @click="resetCampaign" title="Reset campaign to retry">
          <i class="fas fa-redo"></i> Reset &amp; Retry
        </button>
        <button class="dismiss-error-btn" @click="actionError = null">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- Step progress bar -->
      <div class="step-progress">
        <div
          v-for="(step, idx) in steps"
          :key="step.key"
          class="step-item"
          :class="{
            'step-active': activeStep === idx,
            'step-done': isStepDone(idx),
            'step-clickable': canNavigateToStep(idx),
          }"
          @click="canNavigateToStep(idx) && (activeStep = idx)"
        >
          <div class="step-circle">
            <i v-if="isStepDone(idx)" class="fas fa-check"></i>
            <span v-else>{{ idx + 1 }}</span>
          </div>
          <div class="step-label">{{ step.label }}</div>
        </div>
      </div>

      <!-- STEP 1: Research -->
      <div v-if="activeStep === 0" class="step-panel">
        <div class="step-panel-header">
          <h2><i class="fas fa-search"></i> Research Phase</h2>
          <p>The agent researches insurance risks and requirements for <strong>{{ selectedCampaign.business_type }}</strong> and saves the findings to your Knowledge Graph.</p>
        </div>

        <div v-if="!selectedCampaign.research_summary || !selectedCampaign.research_summary.risk_categories" class="step-action-card">
          <!-- Not yet started -->
          <template v-if="selectedCampaign.status !== 'RESEARCHING'">
            <div class="step-action-icon"><i class="fas fa-robot"></i></div>
            <h3>Ready to Research</h3>
            <p>Click "Start Research" to have the AI research insurance requirements, risk categories, and coverage minimums for this business type.</p>
            <button class="btn-primary btn-lg" @click="startResearch">
              <i class="fas fa-play"></i> Start Research
            </button>
          </template>

          <!-- In progress — live activity feed -->
          <template v-else>
            <div class="research-live">
              <div class="research-live-header">
                <div class="research-live-pulse">
                  <span class="pulse-ring"></span>
                  <i class="fas fa-robot"></i>
                </div>
                <div>
                  <h3 class="research-live-title">Agent is researching…</h3>
                  <p class="research-live-subtitle">Analysing insurance requirements for <strong>{{ selectedCampaign.business_type }}</strong></p>
                </div>
              </div>

              <!-- Activity log -->
              <div class="activity-log" ref="activityLog">
                <div
                  v-for="(entry, idx) in progressLog"
                  :key="idx"
                  class="activity-entry"
                  :class="`activity-${entry.step}`"
                >
                  <div class="activity-icon">
                    <i :class="stepIcon(entry.step)"></i>
                  </div>
                  <div class="activity-content">
                    <span class="activity-message">{{ entry.message }}</span>
                    <span class="activity-time">{{ formatLogTime(entry.ts) }}</span>
                  </div>
                </div>

                <!-- Animated "thinking" row while waiting for next real step -->
                <div v-if="showThinkingRow" class="activity-entry activity-thinking">
                  <div class="activity-icon">
                    <span class="thinking-dots"><span></span><span></span><span></span></span>
                  </div>
                  <div class="activity-content">
                    <span class="activity-message thinking-text">{{ currentThinkingMessage }}</span>
                  </div>
                </div>
              </div>

              <!-- Mini stats that appear as data comes in -->
              <div v-if="liveStats && (liveStats.policies || liveStats.risks)" class="live-stats">
                <div v-if="liveStats.policies" class="live-stat">
                  <i class="fas fa-shield-alt"></i>
                  <span>{{ liveStats.policies }} policy types</span>
                </div>
                <div v-if="liveStats.risks" class="live-stat">
                  <i class="fas fa-exclamation-triangle"></i>
                  <span>{{ liveStats.risks }} risk categories</span>
                </div>
                <div v-if="liveStats.regs" class="live-stat">
                  <i class="fas fa-landmark"></i>
                  <span>{{ liveStats.regs }} regulatory notes</span>
                </div>
                <div v-if="liveStats.memories" class="live-stat">
                  <i class="fas fa-brain"></i>
                  <span>{{ liveStats.memories }} memories saved</span>
                </div>
              </div>
            </div>
          </template>
        </div>

        <div v-else class="research-results">
          <div class="results-header">
            <h3><i class="fas fa-check-circle text-success"></i> Research Complete</h3>
            <button class="btn-outline-sm" @click="startResearch">
              <i class="fas fa-redo"></i> Re-run Research
            </button>
          </div>

          <div class="result-summary">
            <p>{{ selectedCampaign.research_summary.executive_summary }}</p>
          </div>

          <div class="result-grid">
            <div class="result-section">
              <h4><i class="fas fa-shield-alt"></i> Required Policy Types</h4>
              <div
                v-for="policy in selectedCampaign.research_summary.required_policy_types"
                :key="policy.policy_type"
                class="result-item"
              >
                <div class="result-item-header">
                  <span class="result-item-name">{{ policy.policy_type }}</span>
                  <span class="badge-mandatory" v-if="policy.is_typically_mandatory">Required</span>
                </div>
                <div class="result-item-detail">{{ policy.typical_coverage_minimum }}</div>
                <div class="result-item-note">{{ policy.why_required }}</div>
              </div>
            </div>

            <div class="result-section">
              <h4><i class="fas fa-exclamation-triangle"></i> Risk Categories</h4>
              <div
                v-for="risk in selectedCampaign.research_summary.risk_categories"
                :key="risk.name"
                class="result-item"
              >
                <div class="result-item-header">
                  <span class="result-item-name">{{ risk.name }}</span>
                  <span class="badge-severity" :class="`severity-${risk.severity?.toLowerCase()}`">
                    {{ risk.severity }}
                  </span>
                </div>
                <div class="result-item-note">{{ risk.description }}</div>
              </div>
            </div>
          </div>

          <div v-if="selectedCampaign.research_summary.regulatory_requirements?.length" class="result-section mt-4">
            <h4><i class="fas fa-landmark"></i> Regulatory Requirements</h4>
            <div
              v-for="reg in selectedCampaign.research_summary.regulatory_requirements"
              :key="reg.requirement"
              class="result-item"
            >
              <span class="result-item-name">{{ reg.jurisdiction }}</span>: {{ reg.requirement }}
            </div>
          </div>

          <div class="step-nav-actions">
            <button class="btn-primary" @click="activeStep = 1">
              Next: Generate Check Definitions <i class="fas fa-arrow-right"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- STEP 2: Generate Check Definitions -->
      <div v-if="activeStep === 1" class="step-panel">
        <div class="step-panel-header">
          <h2><i class="fas fa-cogs"></i> Generate Check Definitions</h2>
          <p>The agent uses the research findings to build a comprehensive set of check requirements with LLM prompt templates ready for document evaluation.</p>
        </div>

        <!-- Research findings inline panel -->
        <div v-if="selectedCampaign.research_summary && selectedCampaign.research_summary.executive_summary" class="research-preview-card">
          <button class="research-preview-toggle" @click="showResearchPanel = !showResearchPanel">
            <span><i class="fas fa-microscope"></i> View Research Findings</span>
            <span class="research-stats-inline">
              <span v-if="selectedCampaign.research_summary.required_policy_types?.length">
                {{ selectedCampaign.research_summary.required_policy_types.length }} policies
              </span>
              <span v-if="selectedCampaign.research_summary.risk_categories?.length">
                · {{ selectedCampaign.research_summary.risk_categories.length }} risks
              </span>
              <span v-if="selectedCampaign.research_summary.regulatory_requirements?.length">
                · {{ selectedCampaign.research_summary.regulatory_requirements.length }} regulations
              </span>
            </span>
            <i :class="showResearchPanel ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" class="toggle-chevron"></i>
          </button>

          <div v-if="showResearchPanel" class="research-preview-body">
            <p class="research-executive-summary">{{ selectedCampaign.research_summary.executive_summary }}</p>

            <div class="research-preview-grid">
              <!-- Policy Types -->
              <div class="rp-section" v-if="selectedCampaign.research_summary.required_policy_types?.length">
                <h5><i class="fas fa-shield-alt"></i> Required Policies</h5>
                <div
                  v-for="p in selectedCampaign.research_summary.required_policy_types"
                  :key="p.policy_type"
                  class="rp-item"
                >
                  <div class="rp-item-row">
                    <strong>{{ p.policy_type }}</strong>
                    <span v-if="p.is_typically_mandatory" class="badge-mandatory">Required</span>
                  </div>
                  <div class="rp-item-coverage" v-if="p.typical_coverage_minimum">{{ p.typical_coverage_minimum }}</div>
                  <div class="rp-item-note" v-if="p.why_required">{{ p.why_required }}</div>
                </div>
              </div>

              <!-- Risk Categories -->
              <div class="rp-section" v-if="selectedCampaign.research_summary.risk_categories?.length">
                <h5><i class="fas fa-exclamation-triangle"></i> Risk Categories</h5>
                <div
                  v-for="r in selectedCampaign.research_summary.risk_categories"
                  :key="r.name"
                  class="rp-item"
                >
                  <div class="rp-item-row">
                    <strong>{{ r.name }}</strong>
                    <span class="badge-severity" :class="`severity-${r.severity?.toLowerCase()}`">{{ r.severity }}</span>
                  </div>
                  <div class="rp-item-note" v-if="r.description">{{ r.description }}</div>
                </div>
              </div>
            </div>

            <!-- Regulatory Requirements -->
            <div v-if="selectedCampaign.research_summary.regulatory_requirements?.length" class="rp-section rp-full">
              <h5><i class="fas fa-landmark"></i> Regulatory Requirements</h5>
              <div
                v-for="reg in selectedCampaign.research_summary.regulatory_requirements"
                :key="reg.requirement"
                class="rp-item rp-item-inline"
              >
                <span class="rp-jurisdiction" v-if="reg.jurisdiction">{{ reg.jurisdiction }}</span>
                {{ reg.requirement }}
              </div>
            </div>

            <!-- Common Exclusions -->
            <div v-if="selectedCampaign.research_summary.common_exclusions_to_watch?.length" class="rp-section rp-full">
              <h5><i class="fas fa-ban"></i> Common Exclusions to Watch</h5>
              <ul class="rp-list">
                <li v-for="ex in selectedCampaign.research_summary.common_exclusions_to_watch" :key="ex">{{ ex }}</li>
              </ul>
            </div>

            <!-- Industry Notes -->
            <div v-if="selectedCampaign.research_summary.industry_specific_notes" class="rp-section rp-full rp-notes">
              <h5><i class="fas fa-sticky-note"></i> Industry Notes</h5>
              <p>{{ selectedCampaign.research_summary.industry_specific_notes }}</p>
            </div>
          </div>
        </div>

        <div v-if="!selectedCampaign.check_definition_id" class="step-action-card">
          <div class="step-action-icon"><i class="fas fa-magic"></i></div>
          <h3>Ready to Generate</h3>
          <p>The AI will create a <strong>ComprehensiveCheckDefinition</strong> with all requirements needed to evaluate insurance documents for this business type.</p>
          <button
            class="btn-primary btn-lg"
            :disabled="selectedCampaign.is_in_progress"
            @click="startGeneration"
          >
            <span v-if="selectedCampaign.status === 'GENERATING'">
              <span class="spinner-sm"></span> Generating…
            </span>
            <span v-else><i class="fas fa-wand-magic-sparkles"></i> Generate Check Definitions</span>
          </button>
        </div>

        <div v-else class="generation-results">
          <div class="results-header">
            <h3><i class="fas fa-check-circle text-success"></i> Check Definition Generated</h3>
            <div class="results-header-actions">
              <button class="btn-outline-sm" @click="startGeneration">
                <i class="fas fa-redo"></i> Regenerate
              </button>
              <a
                :href="thisCheckUrl"
                class="btn-outline-sm"
              >
                <i class="fas fa-flask"></i> Open This check
              </a>
            </div>
          </div>

          <div class="requirements-list">
            <div v-if="loadingRequirements" class="loading-inline">
              <span class="spinner-sm"></span> Loading requirements…
            </div>
            <div v-else>
              <div class="req-count-badge">
                {{ requirements.length }} Requirements Generated
              </div>
              <div
                v-for="req in requirements"
                :key="req.id"
                class="requirement-row"
              >
                <span class="req-severity" :class="`sev-${req.severity?.toLowerCase()}`">
                  {{ req.severity }}
                </span>
                <div class="req-info">
                  <div class="req-code">{{ req.code }}</div>
                  <div class="req-name">{{ req.name }}</div>
                  <div class="req-description">{{ req.description }}</div>
                </div>
                <a
                  :href="labLinkForRequirement(req)"
                  class="req-lab-link"
                  target="_blank"
                  :title="labLinkTitle"
                >
                  <i class="fas fa-flask"></i>
                </a>
              </div>
            </div>
          </div>

          <div class="step-nav-actions">
            <button class="btn-primary" @click="activeStep = 2">
              Next: Upload Test Documents <i class="fas fa-arrow-right"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- STEP 3: Upload & Test -->
      <div v-if="activeStep === 2" class="step-panel">
        <div class="step-panel-header">
          <h2><i class="fas fa-vial"></i> Test Against Documents</h2>
          <p>Upload sample documents to the project and run the check definitions against them to validate your requirements.</p>
        </div>

        <!-- Generated requirements collapsible review -->
        <div v-if="requirements.length || loadingRequirements" class="research-preview-card">
          <button class="research-preview-toggle" @click="showRequirementsPanel = !showRequirementsPanel">
            <span><i class="fas fa-clipboard-list"></i> Review Generated Requirements</span>
            <span class="research-stats-inline">
              {{ requirements.length }} check requirement{{ requirements.length !== 1 ? 's' : '' }}
              <template v-if="requirements.filter(r => r.severity === 'HIGH' || r.severity === 'CRITICAL').length">
                · <span style="color:#ef4444">{{ requirements.filter(r => r.severity === 'HIGH' || r.severity === 'CRITICAL').length }} high/critical</span>
              </template>
            </span>
            <span class="research-stats-inline" style="margin-left:auto;margin-right:0.5rem">
              <a
                :href="thisCheckUrl"
                class="btn-outline-xs"
                @click.stop
              >
                <i class="fas fa-flask"></i> Open This check
              </a>
            </span>
            <i :class="showRequirementsPanel ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" class="toggle-chevron"></i>
          </button>

          <div v-if="showRequirementsPanel" class="research-preview-body">
            <div v-if="loadingRequirements" class="loading-inline">
              <span class="spinner-sm"></span> Loading requirements…
            </div>
            <div v-else>
              <!-- Severity summary strip -->
              <div class="req-severity-strip">
                <span
                  v-for="sev in ['CRITICAL','HIGH','MEDIUM','LOW']"
                  :key="sev"
                  class="req-sev-count"
                  :class="`sev-${sev.toLowerCase()}`"
                >
                  {{ requirements.filter(r => r.severity === sev).length }} {{ sev }}
                </span>
              </div>

              <div
                v-for="req in requirements"
                :key="req.id"
                class="requirement-row"
              >
                <span class="req-severity" :class="`sev-${req.severity?.toLowerCase()}`">
                  {{ req.severity }}
                </span>
                <div class="req-info">
                  <div class="req-code">{{ req.code }}</div>
                  <div class="req-name">{{ req.name }}</div>
                  <div class="req-description">{{ req.description }}</div>
                </div>
                <a
                  :href="labLinkForRequirement(req)"
                  class="req-lab-link"
                  target="_blank"
                  :title="labLinkTitle"
                >
                  <i class="fas fa-flask"></i> Lab
                </a>
              </div>
            </div>
          </div>
        </div>

        <!-- Project selector -->
        <div class="project-section">
          <label class="form-label">Project (documents will be tested from here)</label>
          <div v-if="selectedCampaign.project" class="project-badge">
            <i class="fas fa-folder"></i> Project {{ selectedCampaign.project }}
            <button class="btn-link" @click="showProjectSelector = true">Change</button>
          </div>
          <div v-else class="no-project-warning">
            <i class="fas fa-exclamation-triangle"></i>
            No project assigned.
            <button class="btn-link" @click="showProjectSelector = true">Assign Project</button>
          </div>

          <!-- Simple project selector -->
          <div v-if="showProjectSelector" class="project-selector">
            <select v-model="selectedProjectId" class="form-control">
              <option value="">Select a project…</option>
              <option v-for="proj in projects" :key="proj.id" :value="proj.id">
                {{ proj.name }}
              </option>
            </select>
            <button class="btn-primary-sm" @click="assignProject">Assign</button>
            <button class="btn-ghost-sm" @click="showProjectSelector = false">Cancel</button>
          </div>
        </div>

        <!-- Test trigger -->
        <div class="step-action-card" v-if="!selectedCampaign.test_summary?.total_documents">
          <div class="step-action-icon"><i class="fas fa-play-circle"></i></div>
          <h3>Run Automated Tests</h3>
          <p>The agent will run every check requirement against all documents in the project and produce a consolidated report.</p>
          <button
            class="btn-primary btn-lg"
            :disabled="!selectedCampaign.project || selectedCampaign.is_in_progress"
            @click="startTesting"
          >
            <span v-if="selectedCampaign.status === 'TESTING'">
              <span class="spinner-sm"></span> Testing…
            </span>
            <span v-else><i class="fas fa-bolt"></i> Run Tests</span>
          </button>
        </div>

        <!-- Test results -->
        <div v-else-if="selectedCampaign.test_summary" class="test-results">
          <div class="results-header">
            <h3><i class="fas fa-chart-pie text-success"></i> Test Results</h3>
            <button class="btn-outline-sm" @click="startTesting">
              <i class="fas fa-redo"></i> Re-run Tests
            </button>
          </div>

          <div class="summary-stats">
            <div class="stat-card">
              <div class="stat-value">{{ selectedCampaign.test_summary.total_documents }}</div>
              <div class="stat-label">Documents</div>
            </div>
            <div class="stat-card">
              <div class="stat-value" :class="passRateClass(selectedCampaign.test_summary.overall_pass_rate)">
                {{ selectedCampaign.test_summary.overall_pass_rate }}%
              </div>
              <div class="stat-label">Pass Rate</div>
            </div>
            <div class="stat-card">
              <div class="stat-value">{{ selectedCampaign.test_summary.documents_tested }}</div>
              <div class="stat-label">Tested</div>
            </div>
          </div>

          <!-- Per-document results -->
          <div class="doc-results-table">
            <h4>Document Results</h4>
            <table class="results-table">
              <thead>
                <tr>
                  <th>Document</th>
                  <th>Outcome</th>
                  <th>Pass Rate</th>
                  <th>Passed / Total</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="doc in selectedCampaign.test_summary.document_results"
                  :key="doc.document_id"
                >
                  <td>{{ doc.document_name }}</td>
                  <td>
                    <span class="outcome-badge" :class="`outcome-${doc.outcome?.toLowerCase()}`">
                      {{ doc.outcome || doc.status }}
                    </span>
                  </td>
                  <td>
                    <div class="pass-bar-container">
                      <div class="pass-bar" :style="{ width: doc.pass_rate + '%' }" :class="passRateClass(doc.pass_rate)"></div>
                    </div>
                    <span>{{ doc.pass_rate }}%</span>
                  </td>
                  <td>{{ doc.passed }} / {{ doc.total }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Top failing requirements -->
          <div v-if="selectedCampaign.test_summary.top_failing_requirements?.length" class="failing-reqs">
            <h4><i class="fas fa-exclamation-circle text-warning"></i> Most Commonly Failing Requirements</h4>
            <div
              v-for="req in selectedCampaign.test_summary.top_failing_requirements"
              :key="req.code"
              class="failing-req-row"
            >
              <span class="req-code">{{ req.code }}</span>
              <span class="req-name">{{ req.name }}</span>
              <span class="fail-count">Failed {{ req.fail_count }}x</span>
              <a
                :href="labLinkForRequirement(req)"
                class="btn-outline-xs"
                target="_blank"
              >
                <i class="fas fa-flask"></i> Refine
              </a>
            </div>
          </div>

          <div class="step-nav-actions">
            <button class="btn-primary" @click="activeStep = 3">
              Review & Finalize <i class="fas fa-arrow-right"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- STEP 4: Review -->
      <div v-if="activeStep === 3" class="step-panel">
        <div class="step-panel-header">
          <h2><i class="fas fa-flag-checkered"></i> Campaign Complete</h2>
          <p>Your check definition is ready. Use it in projects, refine individual requirements in the Requirement Lab, or export it as a template.</p>
        </div>

        <div class="completion-summary">
          <div class="completion-icon"><i class="fas fa-trophy"></i></div>
          <h3>{{ selectedCampaign.name }}</h3>
          <p class="business-type-label">{{ selectedCampaign.business_type }}</p>

          <div class="completion-stats">
            <div class="stat-card">
              <div class="stat-value">{{ selectedCampaign.requirement_count }}</div>
              <div class="stat-label">Requirements</div>
            </div>
            <div v-if="selectedCampaign.test_summary?.overall_pass_rate !== undefined" class="stat-card">
              <div class="stat-value" :class="passRateClass(selectedCampaign.test_summary.overall_pass_rate)">
                {{ selectedCampaign.test_summary.overall_pass_rate }}%
              </div>
              <div class="stat-label">Avg Pass Rate</div>
            </div>
          </div>

          <div class="completion-actions">
            <a :href="thisCheckUrl" class="btn-primary">
              <i class="fas fa-flask"></i> Open This check in Lab
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- New Campaign Modal -->
    <div v-if="showNewCampaignModal" class="modal-overlay" @click.self="showNewCampaignModal = false">
      <div class="modal-card">
        <div class="modal-header">
          <h3><i class="fas fa-plus-circle"></i> New Research Campaign</h3>
          <button class="modal-close" @click="showNewCampaignModal = false">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label class="form-label">Campaign Name <span class="required">*</span></label>
            <input
              v-model="newCampaign.name"
              class="form-control"
              placeholder="e.g. Golf Course Builder Insurance Requirements"
            />
          </div>
          <div class="form-group">
            <label class="form-label">Business Type <span class="required">*</span></label>
            <input
              v-model="newCampaign.business_type"
              class="form-control"
              placeholder="e.g. golf course builder, commercial landscaper, HVAC contractor…"
            />
          </div>
          <div class="form-group">
            <label class="form-label">Additional Context (optional)</label>
            <textarea
              v-model="newCampaign.industry_context"
              class="form-control"
              rows="3"
              placeholder="Any specific context about this client or industry (jurisdiction, size, special risks…)"
            ></textarea>
          </div>
          <div class="form-group">
            <label class="form-label">Link to Project (optional)</label>
            <select v-model="newCampaign.project" class="form-control">
              <option value="">Select a project…</option>
              <option v-for="proj in projects" :key="proj.id" :value="proj.id">
                {{ proj.name }}
              </option>
            </select>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="showNewCampaignModal = false">Cancel</button>
          <button
            class="btn-primary"
            :disabled="!newCampaign.name || !newCampaign.business_type || creatingCampaign"
            @click="createCampaign"
          >
            <span v-if="creatingCampaign"><span class="spinner-sm"></span> Creating…</span>
            <span v-else><i class="fas fa-plus"></i> Create Campaign</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';

export default {
  name: 'ResearchCampaignWizard',

  data() {
    return {
      campaigns: [],
      selectedCampaign: null,
      loadingList: false,
      activeStep: 0,
      requirements: [],
      loadingRequirements: false,
      projects: [],
      showNewCampaignModal: false,
      showProjectSelector: false,
      selectedProjectId: '',
      creatingCampaign: false,
      pollingInterval: null,
      pollErrorCount: 0,

      // Live research feed
      progressLog: [],
      showThinkingRow: false,
      thinkingMessages: [
        'Scanning industry regulations and standards…',
        'Cross-referencing common carrier requirements…',
        'Evaluating jurisdictional insurance mandates…',
        'Identifying high-severity risk categories…',
        'Analysing coverage minimums for this sector…',
        'Reviewing historical claims data patterns…',
        'Checking state-level compliance requirements…',
        'Compiling recommended additional coverages…',
      ],
      thinkingIndex: 0,
      thinkingTimer: null,
      liveStats: { policies: 0, risks: 0, regs: 0, memories: 0 },
      actionError: null,
      showResearchPanel: false,
      showRequirementsPanel: false,

      newCampaign: {
        name: '',
        business_type: '',
        industry_context: '',
        project: '',
      },

      steps: [
        { key: 'research', label: 'Research' },
        { key: 'generate', label: 'Generate' },
        { key: 'test', label: 'Test' },
        { key: 'review', label: 'Review' },
      ],
    };
  },

  computed: {
    currentThinkingMessage() {
      return this.thinkingMessages[this.thinkingIndex % this.thinkingMessages.length];
    },
    labLinkTitle() {
      return this.selectedCampaign?.project
        ? 'Open in Check Lab'
        : 'Open This check (assign a project to test against a document)';
    },
    fromProjectId() {
      return this.$route.query.fromProject || this.selectedCampaign?.project || null;
    },
    thisCheckUrl() {
      const projectId = this.fromProjectId;
      const defId = this.selectedCampaign?.check_definition_id;
      if (projectId) {
        return `/projects/${projectId}/lab?tab=check`;
      }
      if (defId) {
        return `/check-definition-editor?id=${defId}`;
      }
      return '/check-definition-editor';
    },
    labBackTo() {
      if (this.fromProjectId) {
        return `/projects/${this.fromProjectId}/lab`;
      }
      return '/projects';
    },
  },

  watch: {
    // Load requirements whenever the user navigates to the Generate or Test step
    activeStep(newStep) {
      if ((newStep === 1 || newStep === 2) &&
          this.selectedCampaign?.check_definition_id &&
          this.requirements.length === 0 &&
          !this.loadingRequirements) {
        this.loadRequirements(this.selectedCampaign.id);
      }
    },
  },

  mounted() {
    this.loadCampaigns();
    this.loadProjects();

    const id = this.$route?.params?.id;
    if (id) {
      this.openCampaignById(id);
    }
  },

  beforeUnmount() {
    this.stopPolling();
  },

  methods: {
    labLinkForRequirement(req) {
      const projectId = this.selectedCampaign?.project;
      const defId = this.selectedCampaign?.check_definition_id;
      if (projectId && req?.id) {
        return `/projects/${projectId}/lab?requirementId=${req.id}`;
      }
      if (projectId) {
        return `/projects/${projectId}/lab`;
      }
      if (defId) {
        return `/check-definition-editor?id=${defId}`;
      }
      return '#';
    },

    // -------------------------------------------------------------------------
    // DATA LOADING
    // -------------------------------------------------------------------------

    async loadCampaigns() {
      this.loadingList = true;
      try {
        const res = await api.get('/research-campaigns/');
        this.campaigns = res.data.results || res.data;
      } catch (err) {
        console.error('Failed to load campaigns:', err);
      } finally {
        this.loadingList = false;
      }
    },

    async loadProjects() {
      try {
        const res = await api.get('/projects/');
        // API may return { projects: [...] }, { results: [...] }, or a bare array
        this.projects = res.data.projects || res.data.results || (Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error('Failed to load projects:', err);
      }
    },

    async openCampaign(campaign) {
      this.selectedCampaign = campaign;
      this.activeStep = this.inferActiveStep(campaign);
      if (campaign.check_definition_id) {
        await this.loadRequirements(campaign.id);
      }
      if (campaign.is_in_progress) {
        this.startPolling();
      }
    },

    async openCampaignById(id) {
      try {
        const res = await api.get(`/research-campaigns/${id}/`);
        await this.openCampaign(res.data);
      } catch (err) {
        console.error('Failed to load campaign:', err);
      }
    },

    async loadRequirements(campaignId) {
      this.loadingRequirements = true;
      try {
        const res = await api.get(`/research-campaigns/${campaignId}/requirements/`);
        this.requirements = res.data.requirements || [];
      } catch (err) {
        console.error('Failed to load requirements:', err);
      } finally {
        this.loadingRequirements = false;
      }
    },

    // -------------------------------------------------------------------------
    // CAMPAIGN CREATION
    // -------------------------------------------------------------------------

    async createCampaign() {
      if (!this.newCampaign.name || !this.newCampaign.business_type) return;
      this.creatingCampaign = true;
      try {
        const res = await api.post('/research-campaigns/', {
          name: this.newCampaign.name,
          business_type: this.newCampaign.business_type,
          industry_context: this.newCampaign.industry_context,
          project: this.newCampaign.project || null,
        });
        this.campaigns.unshift(res.data);
        this.showNewCampaignModal = false;
        this.newCampaign = { name: '', business_type: '', industry_context: '', project: '' };
        await this.openCampaign(res.data);
      } catch (err) {
        console.error('Failed to create campaign:', err);
      } finally {
        this.creatingCampaign = false;
      }
    },

    // -------------------------------------------------------------------------
    // PIPELINE ACTIONS
    // -------------------------------------------------------------------------

    async startResearch() {
      this.actionError = null;
      try {
        // Reset feed state before starting
        this.progressLog = [];
        this.liveStats = { policies: 0, risks: 0, regs: 0, memories: 0 };

        const res = await api.post(`/research-campaigns/${this.selectedCampaign.id}/research/`);
        this.selectedCampaign.status = res.data.status;
        this.selectedCampaign.celery_task_id = res.data.task_id;
        this.startPolling();
      } catch (err) {
        const msg = err?.response?.data?.error || err?.message || 'Failed to start research.';
        this.actionError = msg;
        console.error('Failed to start research:', err);
      }
    },

    async startGeneration() {
      this.actionError = null;
      try {
        const res = await api.post(`/research-campaigns/${this.selectedCampaign.id}/generate/`);
        this.selectedCampaign.status = res.data.status;
        this.selectedCampaign.celery_task_id = res.data.task_id;
        this.startPolling();
      } catch (err) {
        const msg = err?.response?.data?.error || err?.message || 'Failed to start generation.';
        this.actionError = msg;
        console.error('Failed to start generation:', err);
      }
    },

    async startTesting() {
      this.actionError = null;
      try {
        const res = await api.post(`/research-campaigns/${this.selectedCampaign.id}/test/`);
        this.selectedCampaign.status = res.data.status;
        this.selectedCampaign.celery_task_id = res.data.task_id;
        this.startPolling();
      } catch (err) {
        const msg = err?.response?.data?.error || err?.message || 'Failed to start testing.';
        this.actionError = msg;
        console.error('Failed to start testing:', err);
      }
    },

    async resetCampaign() {
      this.actionError = null;
      try {
        const res = await api.post(`/research-campaigns/${this.selectedCampaign.id}/reset/`);
        Object.assign(this.selectedCampaign, { status: res.data.status, celery_task_id: '', error_message: '' });
        this.activeStep = this.inferActiveStep(this.selectedCampaign);
      } catch (err) {
        const msg = err?.response?.data?.error || err?.message || 'Reset failed.';
        this.actionError = msg;
        console.error('Reset failed:', err);
      }
    },

    async assignProject() {
      if (!this.selectedProjectId) return;
      try {
        const res = await api.patch(`/research-campaigns/${this.selectedCampaign.id}/`, {
          project: this.selectedProjectId,
        });
        this.selectedCampaign.project = res.data.project;
        this.showProjectSelector = false;
        this.selectedProjectId = '';
      } catch (err) {
        console.error('Failed to assign project:', err);
      }
    },

    // -------------------------------------------------------------------------
    // POLLING
    // -------------------------------------------------------------------------

    startPolling() {
      this.stopPolling();
      this.pollErrorCount = 0;
      // 5-second interval — avoids hammering the server when a Celery task
      // temporarily blocks Django's dev-server worker thread
      this.pollingInterval = setInterval(() => this.pollStatus(), 5000);
      // Start rotating thinking messages
      this.startThinkingRotation();
    },

    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
      this.pollErrorCount = 0;
      this.stopThinkingRotation();
    },

    async pollStatus() {
      if (!this.selectedCampaign) {
        this.stopPolling();
        return;
      }
      try {
        const res = await api.get(`/research-campaigns/${this.selectedCampaign.id}/status/`);
        const updated = res.data;

        // Reset error backoff on success
        this.pollErrorCount = 0;

        // Sync progress log from backend
        const backendLog = updated.research_summary?._progress_log || [];
        if (backendLog.length > (this.progressLog?.length || 0)) {
          this.progressLog = backendLog;
          this.updateLiveStats(updated.research_summary);
          this.$nextTick(() => this.scrollActivityLog());
        }

        // Update campaign data
        Object.assign(this.selectedCampaign, updated);

        if (!updated.is_in_progress) {
          this.stopPolling();
          this.showThinkingRow = false;

          // Load requirements if we just finished generation
          if (updated.check_definition_id && this.requirements.length === 0) {
            await this.loadRequirements(this.selectedCampaign.id);
          }

          // Advance step if completed
          this.activeStep = this.inferActiveStep(updated);
        }
      } catch (err) {
        this.pollErrorCount = (this.pollErrorCount || 0) + 1;
        // ERR_EMPTY_RESPONSE / ERR_NETWORK usually means Django is busy
        // processing the Celery task synchronously (ALWAYS_EAGER mode in dev).
        // Suppress noisy console spam after the first failure; the UI keeps
        // showing the thinking animation so the user knows work is happening.
        if (this.pollErrorCount === 1) {
          console.warn('Status poll failed (will keep retrying silently):', err?.message || err);
        }
        // If we've had many consecutive failures, slow down to 15s to give
        // the backend breathing room.
        if (this.pollErrorCount === 5) {
          clearInterval(this.pollingInterval);
          this.pollingInterval = setInterval(() => this.pollStatus(), 15000);
        }
      }
    },

    startThinkingRotation() {
      this.showThinkingRow = true;
      this.thinkingIndex = 0;
      this.thinkingTimer = setInterval(() => {
        this.thinkingIndex = (this.thinkingIndex + 1) % this.thinkingMessages.length;
      }, 3500);
    },

    stopThinkingRotation() {
      if (this.thinkingTimer) {
        clearInterval(this.thinkingTimer);
        this.thinkingTimer = null;
      }
      this.showThinkingRow = false;
    },

    updateLiveStats(summary) {
      if (!summary) return;
      this.liveStats = {
        policies: summary.required_policy_types?.length || 0,
        risks: summary.risk_categories?.length || 0,
        regs: summary.regulatory_requirements?.length || 0,
        memories: summary._progress_log?.filter(e => e.step === 'memory_done').length > 0
          ? (summary.required_policy_types?.length || 0) + (summary.risk_categories?.length || 0) + 1
          : 0,
      };
    },

    scrollActivityLog() {
      const el = this.$refs.activityLog;
      if (el) el.scrollTop = el.scrollHeight;
    },

    // -------------------------------------------------------------------------
    // HELPERS
    // -------------------------------------------------------------------------

    inferActiveStep(campaign) {
      if (campaign.test_summary?.total_documents > 0) return 3;
      if (campaign.check_definition_id) return 2;
      if (campaign.research_summary?.risk_categories?.length > 0) return 1;
      return 0;
    },

    isStepDone(idx) {
      const c = this.selectedCampaign;
      if (!c) return false;
      if (idx === 0) return !!(c.research_summary?.risk_categories?.length > 0);
      if (idx === 1) return !!c.check_definition_id;
      if (idx === 2) return !!(c.test_summary?.total_documents > 0);
      if (idx === 3) return c.status === 'COMPLETE';
      return false;
    },

    canNavigateToStep(idx) {
      if (idx === 0) return true;
      if (idx === 1) return this.isStepDone(0);
      if (idx === 2) return this.isStepDone(1);
      if (idx === 3) return this.isStepDone(2);
      return false;
    },

    statusLabel(s) {
      const labels = {
        DRAFT: 'Draft',
        RESEARCHING: 'Researching…',
        RESEARCH_COMPLETE: 'Research Complete',
        GENERATING: 'Generating…',
        GENERATION_COMPLETE: 'Generated',
        TESTING: 'Testing…',
        COMPLETE: 'Complete',
        FAILED: 'Failed',
      };
      return labels[s] || s;
    },

    statusClass(s) {
      if (['RESEARCHING', 'GENERATING', 'TESTING'].includes(s)) return 'status-progress';
      if (s === 'COMPLETE') return 'status-complete';
      if (s === 'FAILED') return 'status-failed';
      if (['RESEARCH_COMPLETE', 'GENERATION_COMPLETE'].includes(s)) return 'status-partial';
      return 'status-draft';
    },

    statusIcon(s) {
      if (['RESEARCHING', 'GENERATING', 'TESTING'].includes(s)) return 'fas fa-circle-notch fa-spin';
      if (s === 'COMPLETE') return 'fas fa-check-circle';
      if (s === 'FAILED') return 'fas fa-times-circle';
      if (['RESEARCH_COMPLETE', 'GENERATION_COMPLETE'].includes(s)) return 'fas fa-check';
      return 'fas fa-circle';
    },

    passRateClass(rate) {
      if (rate >= 80) return 'pass-high';
      if (rate >= 50) return 'pass-mid';
      return 'pass-low';
    },

    stepIcon(step) {
      const icons = {
        start:       'fas fa-play-circle',
        llm:         'fas fa-brain',
        llm_done:    'fas fa-check-circle',
        kg_save:     'fas fa-database',
        kg_done:     'fas fa-check-circle',
        memory:      'fas fa-memory',
        memory_done: 'fas fa-check-circle',
        error:       'fas fa-exclamation-circle',
      };
      return icons[step] || 'fas fa-circle';
    },

    formatLogTime(ts) {
      if (!ts) return '';
      try {
        return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      } catch {
        return '';
      }
    },
  },
};
</script>

<style scoped>
/* ============================================================
   Layout
============================================================ */
.research-campaign-wizard {
  /* Page chrome: global .entity-list-page */
  padding-bottom: 2.5rem;
  background: transparent;
}

.wizard-header {
  background: transparent;
  border-bottom: none;
  padding: 0;
  position: static;
  margin-bottom: 1.15rem;
}

.header-actions {
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
}

.wizard-title-group {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.wizard-title {
  font-size: 1.85rem;
  font-weight: 700;
  line-height: 1.2;
  color: #0f172a;
  margin: 0.25rem 0 0.2rem;
}

.wizard-subtitle {
  margin: 0;
  color: #64748b;
  font-size: 0.95rem;
}

.back-btn {
  margin-top: 0.35rem;
  width: 2rem;
  height: 2rem;
  border-radius: 0.45rem;
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #475569;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.back-btn:hover {
  border-color: #cbd5e1;
  color: var(--p-accent, #1e3a5f);
}

/* ============================================================
   Campaign List
============================================================ */
.campaign-list-view {
  max-width: none;
  margin: 0;
  padding: 0;
}

.campaign-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.25rem;
}

.campaign-card {
  background: white;
  border: 1px solid var(--border-color, #e2e8f0);
  border-radius: 12px;
  padding: 1.5rem;
  cursor: pointer;
  transition: box-shadow 0.15s, border-color 0.15s;
}

.campaign-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  border-color: var(--primary-color, #4f46e5);
}

.campaign-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
}

.campaign-card-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary, #1a202c);
  margin: 0 0 0.4rem;
}

.campaign-card-business {
  font-size: 0.875rem;
  color: var(--text-secondary, #64748b);
  margin: 0 0 0.75rem;
}

.campaign-card-meta {
  display: flex;
  gap: 1rem;
  font-size: 0.8rem;
  color: var(--text-muted, #94a3b8);
}

/* ============================================================
   Campaign Detail / Step Wizard
============================================================ */
.campaign-detail {
  max-width: none;
  margin: 0;
  padding: 0;
}

.action-error-banner {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: #fef2f2;
  border: 1px solid #fca5a5;
  color: #991b1b;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  margin-bottom: 1.25rem;
  font-size: 0.9rem;
}
.action-error-banner i.fa-exclamation-triangle {
  flex-shrink: 0;
  color: #ef4444;
}
.action-error-banner span {
  flex: 1;
}
.reset-inline-btn {
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.3rem 0.75rem;
  font-size: 0.8rem;
  cursor: pointer;
  white-space: nowrap;
}
.reset-inline-btn:hover {
  background: #dc2626;
}
.dismiss-error-btn {
  background: none;
  border: none;
  color: #991b1b;
  cursor: pointer;
  padding: 0.2rem 0.4rem;
  font-size: 0.9rem;
  flex-shrink: 0;
}
.dismiss-error-btn:hover {
  color: #7f1d1d;
}

.step-progress {
  display: flex;
  align-items: center;
  background: white;
  border: 1px solid var(--border-color, #e2e8f0);
  border-radius: 12px;
  padding: 1rem 2rem;
  margin-bottom: 2rem;
  gap: 0;
}

.step-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4rem;
  position: relative;
  cursor: default;
}

.step-item::after {
  content: '';
  position: absolute;
  top: 18px;
  right: -50%;
  width: 100%;
  height: 2px;
  background: var(--border-color, #e2e8f0);
}

.step-item:last-child::after {
  display: none;
}

.step-item.step-done::after {
  background: var(--primary-color, #4f46e5);
}

.step-circle {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 2px solid var(--border-color, #e2e8f0);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  font-weight: 600;
  background: white;
  color: var(--text-muted, #94a3b8);
  z-index: 1;
}

.step-active .step-circle {
  border-color: var(--primary-color, #4f46e5);
  color: var(--primary-color, #4f46e5);
  background: #eef2ff;
}

.step-done .step-circle {
  border-color: var(--primary-color, #4f46e5);
  background: var(--primary-color, #4f46e5);
  color: white;
}

.step-clickable {
  cursor: pointer;
}

.step-clickable:hover .step-circle {
  background: #eef2ff;
}

.step-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--text-muted, #94a3b8);
}

.step-active .step-label {
  color: var(--primary-color, #4f46e5);
  font-weight: 600;
}

.step-done .step-label {
  color: var(--text-secondary, #64748b);
}

/* ============================================================
   Step Panels
============================================================ */
.step-panel {
  background: white;
  border: 1px solid var(--border-color, #e2e8f0);
  border-radius: 12px;
  padding: 2rem;
}

.step-panel-header {
  margin-bottom: 2rem;
}

.step-panel-header h2 {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--text-primary, #1a202c);
  margin: 0 0 0.5rem;
}

.step-panel-header h2 i {
  color: var(--primary-color, #4f46e5);
  margin-right: 0.5rem;
}

.step-panel-header p {
  color: var(--text-secondary, #64748b);
  margin: 0;
}

.step-action-card {
  text-align: center;
  padding: 3rem 2rem;
  border: 2px dashed var(--border-color, #e2e8f0);
  border-radius: 12px;
  background: #f8fafc;
}

.step-action-icon {
  font-size: 3rem;
  color: var(--primary-color, #4f46e5);
  margin-bottom: 1rem;
}

.step-action-card h3 {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 0.5rem;
}

.step-action-card p {
  color: var(--text-secondary, #64748b);
  max-width: 500px;
  margin: 0 auto 1.5rem;
}

/* ============================================================
   Research Results
============================================================ */
.results-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.results-header h3 {
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
}

.results-header-actions {
  display: flex;
  gap: 0.5rem;
}

/* ── Research Preview Panel (step 2 inline view) ──────────────────────── */
.research-preview-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  margin-bottom: 1.5rem;
  overflow: hidden;
}
.research-preview-toggle {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.9rem 1.25rem;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 600;
  color: #1e293b;
  text-align: left;
}
.research-preview-toggle:hover { background: #f1f5f9; }
.research-preview-toggle i.fa-microscope { color: #6366f1; }
.research-stats-inline {
  flex: 1;
  font-size: 0.82rem;
  font-weight: 400;
  color: #64748b;
}
.toggle-chevron {
  color: #94a3b8;
  font-size: 0.8rem;
  margin-left: auto;
}
.research-preview-body {
  padding: 1.25rem 1.5rem 1.5rem;
  border-top: 1px solid #e2e8f0;
}
.research-executive-summary {
  font-size: 0.9rem;
  color: #475569;
  line-height: 1.6;
  margin-bottom: 1.25rem;
  font-style: italic;
}
.research-preview-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-bottom: 1.25rem;
}
@media (max-width: 700px) {
  .research-preview-grid { grid-template-columns: 1fr; }
}
.rp-section h5 {
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #64748b;
  margin: 0 0 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
}
.rp-section h5 i { color: #6366f1; }
.rp-full { margin-bottom: 1rem; }
.rp-item {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 0.6rem 0.75rem;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
}
.rp-item-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.25rem;
}
.rp-item-coverage {
  font-size: 0.8rem;
  color: #0ea5e9;
  font-weight: 600;
  margin-bottom: 0.2rem;
}
.rp-item-note {
  font-size: 0.8rem;
  color: #64748b;
  line-height: 1.4;
}
.rp-item-inline {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.rp-jurisdiction {
  background: #e0e7ff;
  color: #3730a3;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.15rem 0.5rem;
  border-radius: 4px;
  white-space: nowrap;
}
.rp-list {
  margin: 0;
  padding-left: 1.25rem;
  font-size: 0.875rem;
  color: #475569;
  line-height: 1.7;
}
.rp-notes p {
  font-size: 0.875rem;
  color: #475569;
  line-height: 1.6;
  margin: 0;
}

.result-summary {
  background: #f8fafc;
  border-radius: 8px;
  padding: 1rem 1.25rem;
  color: var(--text-secondary, #64748b);
  margin-bottom: 1.5rem;
  font-size: 0.9rem;
}

.result-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
}

@media (max-width: 767px) {
  .result-grid { grid-template-columns: 1fr; }
}

.result-section {
  background: #f8fafc;
  border-radius: 10px;
  padding: 1.25rem;
}

.result-section h4 {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary, #1a202c);
  margin: 0 0 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.result-item {
  margin-bottom: 0.875rem;
  padding-bottom: 0.875rem;
  border-bottom: 1px solid var(--border-color, #e2e8f0);
}

.result-item:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.result-item-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.25rem;
}

.result-item-name {
  font-weight: 600;
  font-size: 0.875rem;
}

.result-item-detail {
  font-size: 0.8rem;
  color: var(--primary-color, #4f46e5);
  font-weight: 500;
}

.result-item-note {
  font-size: 0.8rem;
  color: var(--text-secondary, #64748b);
  margin-top: 0.2rem;
}

/* ============================================================
   Requirements List
============================================================ */
.btn-outline-xs {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.75rem;
  padding: 0.2rem 0.6rem;
  border: 1px solid #cbd5e1;
  border-radius: 5px;
  color: #475569;
  background: white;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
}
.btn-outline-xs:hover {
  background: #f1f5f9;
  border-color: #94a3b8;
}

.req-severity-strip {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
}
.req-sev-count {
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.req-sev-count.sev-critical { background: #fef2f2; color: #dc2626; border: 1px solid #fca5a5; }
.req-sev-count.sev-high     { background: #fff7ed; color: #ea580c; border: 1px solid #fdba74; }
.req-sev-count.sev-medium   { background: #fefce8; color: #ca8a04; border: 1px solid #fde047; }
.req-sev-count.sev-low      { background: #f0fdf4; color: #16a34a; border: 1px solid #86efac; }

.req-count-badge {
  background: #eef2ff;
  color: var(--primary-color, #4f46e5);
  border-radius: 20px;
  padding: 0.35rem 1rem;
  font-size: 0.8rem;
  font-weight: 600;
  display: inline-block;
  margin-bottom: 1rem;
}

.requirement-row {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 0.875rem;
  border: 1px solid var(--border-color, #e2e8f0);
  border-radius: 8px;
  margin-bottom: 0.5rem;
  background: #fafafa;
}

.req-severity {
  font-size: 0.65rem;
  font-weight: 700;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  white-space: nowrap;
  flex-shrink: 0;
}

.sev-critical { background: #fee2e2; color: #dc2626; }
.sev-high     { background: #fef3c7; color: #d97706; }
.sev-medium   { background: #dbeafe; color: #2563eb; }
.sev-low      { background: #f0fdf4; color: #16a34a; }

.req-info {
  flex: 1;
  min-width: 0;
}

.req-code {
  font-size: 0.7rem;
  font-weight: 700;
  color: var(--text-muted, #94a3b8);
  font-family: monospace;
}

.req-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary, #1a202c);
}

.req-description {
  font-size: 0.8rem;
  color: var(--text-secondary, #64748b);
  margin-top: 0.2rem;
}

.req-lab-link {
  color: var(--primary-color, #4f46e5);
  text-decoration: none;
  font-size: 0.875rem;
  flex-shrink: 0;
  padding: 0.25rem;
}

/* ============================================================
   Test Results
============================================================ */
.summary-stats {
  display: flex;
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.stat-card {
  background: #f8fafc;
  border-radius: 10px;
  padding: 1.25rem 1.5rem;
  text-align: center;
  flex: 1;
}

.stat-value {
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-primary, #1a202c);
}

.stat-label {
  font-size: 0.8rem;
  color: var(--text-muted, #94a3b8);
  margin-top: 0.25rem;
}

.pass-high { color: #16a34a; }
.pass-mid  { color: #d97706; }
.pass-low  { color: #dc2626; }

.results-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.results-table th {
  text-align: left;
  padding: 0.6rem 0.75rem;
  background: #f8fafc;
  font-weight: 600;
  color: var(--text-secondary, #64748b);
  border-bottom: 2px solid var(--border-color, #e2e8f0);
}

.results-table td {
  padding: 0.75rem;
  border-bottom: 1px solid var(--border-color, #e2e8f0);
  vertical-align: middle;
}

.doc-results-table {
  margin-bottom: 2rem;
}

.doc-results-table h4 {
  font-size: 0.875rem;
  font-weight: 600;
  margin: 0 0 0.75rem;
}

.pass-bar-container {
  height: 4px;
  background: #e2e8f0;
  border-radius: 2px;
  overflow: hidden;
  margin-bottom: 2px;
}

.pass-bar {
  height: 100%;
  border-radius: 2px;
}

.pass-bar.pass-high { background: #16a34a; }
.pass-bar.pass-mid  { background: #d97706; }
.pass-bar.pass-low  { background: #dc2626; }

.outcome-badge {
  font-size: 0.7rem;
  font-weight: 700;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
}

.outcome-pass    { background: #f0fdf4; color: #16a34a; }
.outcome-fail    { background: #fee2e2; color: #dc2626; }
.outcome-needs_review { background: #fef3c7; color: #d97706; }

.failing-reqs {
  background: #fffbeb;
  border: 1px solid #fef3c7;
  border-radius: 10px;
  padding: 1.25rem;
  margin-top: 1.5rem;
}

.failing-reqs h4 {
  font-size: 0.875rem;
  font-weight: 600;
  margin: 0 0 1rem;
}

.failing-req-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid #fef3c7;
  font-size: 0.875rem;
}

.failing-req-row:last-child {
  border-bottom: none;
}

.fail-count {
  margin-left: auto;
  font-size: 0.8rem;
  color: #d97706;
  font-weight: 600;
}

/* ============================================================
   Completion
============================================================ */
.completion-summary {
  text-align: center;
  padding: 3rem 2rem;
}

.completion-icon {
  font-size: 4rem;
  color: #f59e0b;
  margin-bottom: 1rem;
}

.completion-summary h3 {
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 0.25rem;
}

.business-type-label {
  color: var(--text-secondary, #64748b);
  margin-bottom: 2rem;
}

.completion-stats {
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin-bottom: 2rem;
}

.completion-actions {
  display: flex;
  justify-content: center;
  gap: 1rem;
}

/* ============================================================
   Status Badges
============================================================ */
.campaign-status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.2rem 0.6rem;
  border-radius: 20px;
}

.status-draft    { background: #f1f5f9; color: #64748b; }
.status-progress { background: #eef2ff; color: #4f46e5; }
.status-partial  { background: #dbeafe; color: #2563eb; }
.status-complete { background: #f0fdf4; color: #16a34a; }
.status-failed   { background: #fee2e2; color: #dc2626; }

.badge-mandatory {
  font-size: 0.65rem;
  font-weight: 700;
  padding: 0.15rem 0.4rem;
  background: #fee2e2;
  color: #dc2626;
  border-radius: 4px;
}

.badge-severity {
  font-size: 0.65rem;
  font-weight: 700;
  padding: 0.15rem 0.4rem;
  border-radius: 4px;
}

.severity-high    { background: #fef3c7; color: #d97706; }
.severity-medium  { background: #dbeafe; color: #2563eb; }
.severity-low     { background: #f0fdf4; color: #16a34a; }
.severity-critical { background: #fee2e2; color: #dc2626; }

/* ============================================================
   Modal
============================================================ */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.modal-card {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 500px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid var(--border-color, #e2e8f0);
}

.modal-header h3 {
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
}

.modal-body {
  padding: 1.5rem;
}

.modal-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--border-color, #e2e8f0);
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}

/* ============================================================
   Forms
============================================================ */
.form-group {
  margin-bottom: 1.25rem;
}

.form-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-primary, #1a202c);
  margin-bottom: 0.4rem;
}

.required {
  color: #dc2626;
}

.form-control {
  width: 100%;
  padding: 0.6rem 0.875rem;
  border: 1px solid var(--border-color, #e2e8f0);
  border-radius: 8px;
  font-size: 0.875rem;
  color: var(--text-primary, #1a202c);
  background: white;
  transition: border-color 0.15s;
  box-sizing: border-box;
}

.form-control:focus {
  outline: none;
  border-color: var(--primary-color, #4f46e5);
  box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
}

/* ============================================================
   Buttons
============================================================ */
.btn-primary {
  background: var(--primary-color, #4f46e5);
  color: white;
  border: none;
  padding: 0.6rem 1.25rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  text-decoration: none;
  transition: background 0.15s;
}

.btn-primary:hover { background: #4338ca; }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }

.btn-primary-sm {
  background: var(--primary-color, #4f46e5);
  color: white;
  border: none;
  padding: 0.4rem 0.875rem;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
}

.btn-lg {
  padding: 0.875rem 2rem;
  font-size: 1rem;
}

.btn-outline {
  background: white;
  color: var(--primary-color, #4f46e5);
  border: 1.5px solid var(--primary-color, #4f46e5);
  padding: 0.6rem 1.25rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  text-decoration: none;
}

.btn-outline:hover { background: #eef2ff; }

.btn-outline-sm {
  background: white;
  color: var(--text-secondary, #64748b);
  border: 1px solid var(--border-color, #e2e8f0);
  padding: 0.4rem 0.875rem;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  text-decoration: none;
}

.btn-outline-xs {
  background: white;
  color: var(--primary-color, #4f46e5);
  border: 1px solid var(--primary-color, #4f46e5);
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  text-decoration: none;
}

.btn-ghost {
  background: transparent;
  color: var(--text-secondary, #64748b);
  border: none;
  padding: 0.6rem 1.25rem;
  border-radius: 8px;
  font-size: 0.875rem;
  cursor: pointer;
}

.btn-ghost-sm {
  background: transparent;
  color: var(--text-secondary, #64748b);
  border: none;
  padding: 0.4rem 0.875rem;
  border-radius: 6px;
  font-size: 0.8rem;
  cursor: pointer;
}

.btn-link {
  background: none;
  border: none;
  color: var(--primary-color, #4f46e5);
  cursor: pointer;
  font-size: 0.875rem;
  padding: 0;
  text-decoration: underline;
}

.back-btn {
  background: #f1f5f9;
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary, #64748b);
  font-size: 0.875rem;
}

.modal-close {
  background: none;
  border: none;
  font-size: 1rem;
  cursor: pointer;
  color: var(--text-muted, #94a3b8);
  padding: 0.25rem;
}

/* ============================================================
   Step navigation
============================================================ */
.step-nav-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--border-color, #e2e8f0);
}

/* ============================================================
   Project section
============================================================ */
.project-section {
  background: #f8fafc;
  border-radius: 10px;
  padding: 1.25rem;
  margin-bottom: 1.5rem;
}

.project-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: white;
  border: 1px solid var(--border-color, #e2e8f0);
  border-radius: 8px;
  padding: 0.5rem 0.875rem;
  font-size: 0.875rem;
}

.no-project-warning {
  color: #d97706;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.project-selector {
  margin-top: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.project-selector .form-control {
  flex: 1;
}

/* ============================================================
   Misc
============================================================ */
.spinner-sm {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: currentColor;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.text-success { color: #16a34a; }
.text-warning { color: #d97706; }
.mt-4 { margin-top: 1.5rem; }

.loading-state {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.skeleton-card {
  height: 150px;
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 12px;
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.empty-state {
  text-align: center;
  padding: 4rem 2rem;
  color: var(--text-secondary, #64748b);
}

.empty-icon {
  font-size: 3rem;
  color: var(--text-muted, #94a3b8);
  margin-bottom: 1rem;
}

.empty-state h3 {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary, #1a202c);
  margin: 0 0 0.5rem;
}

.loading-inline {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-secondary, #64748b);
  font-size: 0.875rem;
  padding: 1rem 0;
}

/* ============================================================
   Live Research Feed
============================================================ */
.research-live {
  border: 1px solid #e0e7ff;
  border-radius: 12px;
  background: #fafbff;
  padding: 1.5rem;
  text-align: left;
}

.research-live-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.research-live-pulse {
  position: relative;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  color: var(--primary-color, #4f46e5);
  flex-shrink: 0;
}

.pulse-ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 2px solid var(--primary-color, #4f46e5);
  opacity: 0.4;
  animation: pulse-expand 1.8s ease-out infinite;
}

@keyframes pulse-expand {
  0%   { transform: scale(0.8); opacity: 0.6; }
  100% { transform: scale(1.6); opacity: 0; }
}

.research-live-title {
  font-size: 1rem;
  font-weight: 700;
  color: var(--text-primary, #1a202c);
  margin: 0 0 0.2rem;
}

.research-live-subtitle {
  font-size: 0.8rem;
  color: var(--text-secondary, #64748b);
  margin: 0;
}

/* Activity log */
.activity-log {
  background: #1e1e2e;
  border-radius: 8px;
  padding: 1rem;
  min-height: 160px;
  max-height: 280px;
  overflow-y: auto;
  margin-bottom: 1rem;
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 0.8rem;
}

.activity-entry {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  padding: 0.35rem 0;
  animation: fadeSlideIn 0.3s ease;
}

@keyframes fadeSlideIn {
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
}

.activity-icon {
  width: 18px;
  flex-shrink: 0;
  margin-top: 1px;
  font-size: 0.75rem;
}

.activity-start     .activity-icon { color: #a78bfa; }
.activity-llm       .activity-icon { color: #60a5fa; }
.activity-llm_done  .activity-icon { color: #34d399; }
.activity-kg_save   .activity-icon { color: #fbbf24; }
.activity-kg_done   .activity-icon { color: #34d399; }
.activity-memory    .activity-icon { color: #f472b6; }
.activity-memory_done .activity-icon { color: #34d399; }
.activity-error     .activity-icon { color: #f87171; }
.activity-thinking  .activity-icon { color: #94a3b8; }

.activity-content {
  display: flex;
  align-items: baseline;
  gap: 0.75rem;
  flex: 1;
  flex-wrap: wrap;
}

.activity-message {
  color: #e2e8f0;
  line-height: 1.4;
}

.activity-start     .activity-message { color: #a78bfa; }
.activity-llm_done  .activity-message,
.activity-kg_done   .activity-message,
.activity-memory_done .activity-message { color: #34d399; }
.activity-error     .activity-message { color: #f87171; }
.activity-thinking  .activity-message { color: #64748b; font-style: italic; }

.activity-time {
  font-size: 0.7rem;
  color: #475569;
  white-space: nowrap;
  flex-shrink: 0;
}

/* Thinking dots */
.thinking-dots {
  display: inline-flex;
  gap: 3px;
  align-items: center;
}

.thinking-dots span {
  display: inline-block;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #64748b;
  animation: dot-bounce 1.2s infinite;
}

.thinking-dots span:nth-child(2) { animation-delay: 0.2s; }
.thinking-dots span:nth-child(3) { animation-delay: 0.4s; }

@keyframes dot-bounce {
  0%, 80%, 100% { transform: scale(0.7); opacity: 0.4; }
  40%            { transform: scale(1);   opacity: 1; }
}

.thinking-text {
  animation: textFade 3.5s ease infinite;
}

@keyframes textFade {
  0%, 100% { opacity: 0.5; }
  50%       { opacity: 1; }
}

/* Live stats strip */
.live-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #e0e7ff;
}

.live-stat {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: white;
  border: 1px solid #e0e7ff;
  border-radius: 20px;
  padding: 0.25rem 0.75rem;
  font-size: 0.775rem;
  font-weight: 600;
  color: var(--primary-color, #4f46e5);
  animation: fadeSlideIn 0.4s ease;
}

.live-stat i {
  font-size: 0.75rem;
  opacity: 0.8;
}
</style>
